import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { faithService } from '../services/api';
import Navigation from '../components/Navigation';
import { Button, Card, Input } from '../components/ui';
import { FAITH_TRADITION_OPTIONS, FAITH_TRADITIONS } from '../constants/faithTraditions';

const FaithContentAdmin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('create'); // create, list, bulk
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [contentList, setContentList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // Create/Edit form state
  const [formData, setFormData] = useState({
    day: '',
    phase: '',
    tradition: FAITH_TRADITIONS.CHRISTIAN,
    title: '',
    scriptureReference: '',
    scriptureText: '',
    teachingReference: '',
    sourceTeaching: '',
    devotionalContent: '',
    reflectionPrompt: '',
    prayerPrompt: '',
    recoveryConnection: '',
    themeTag: 'grace',
    estimatedTime: '10',
    status: 'draft'
  });

  // Bulk import state
  const [bulkItems, setBulkItems] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null);

  // Validation
  const validateForm = () => {
    const errors = [];
    if (!formData.day || formData.day < 1 || formData.day > 365) errors.push('Day must be 1-365');
    if (!formData.phase || formData.phase < 1 || formData.phase > 4) errors.push('Phase must be 1-4');
    if (!formData.title.trim()) errors.push('Title is required');
    const hasScripturePair = Boolean(formData.scriptureReference.trim() && formData.scriptureText.trim());
    const hasTeachingPair = Boolean(formData.teachingReference.trim() && formData.sourceTeaching.trim());
    if (!hasScripturePair && !hasTeachingPair) {
      errors.push('Provide scripture fields or teaching fields');
    }
    if (!formData.devotionalContent.trim()) errors.push('Devotional content is required');
    if (formData.scriptureText.length > 2000) errors.push('Scripture text limited to 2000 characters');
    if (formData.sourceTeaching.length > 2000) errors.push('Source teaching limited to 2000 characters');
    if (formData.devotionalContent.length > 3000) errors.push('Devotional content limited to 3000 characters');
    return errors;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    setMessage(null);

    const errors = validateForm();
    if (errors.length > 0) {
      setMessage({ type: 'error', text: errors.join(', ') });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        day: parseInt(formData.day),
        phase: parseInt(formData.phase),
        estimatedTime: parseInt(formData.estimatedTime)
      };
      await faithService.createContent(payload);
      setMessage({ type: 'success', text: 'Faith content created successfully!' });
      setFormData({
        day: '',
        phase: '',
        tradition: FAITH_TRADITIONS.CHRISTIAN,
        title: '',
        scriptureReference: '',
        scriptureText: '',
        teachingReference: '',
        sourceTeaching: '',
        devotionalContent: '',
        reflectionPrompt: '',
        prayerPrompt: '',
        recoveryConnection: '',
        themeTag: 'grace',
        estimatedTime: '10',
        status: 'draft'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to create content'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!bulkItems.trim()) {
      setMessage({ type: 'error', text: 'Please provide content items' });
      return;
    }

    setLoading(true);
    try {
      let items;
      try {
        items = JSON.parse(bulkItems);
      } catch {
        setMessage({ type: 'error', text: 'Invalid JSON format' });
        setLoading(false);
        return;
      }

      if (!Array.isArray(items)) {
        setMessage({ type: 'error', text: 'JSON must be an array of items' });
        setLoading(false);
        return;
      }

      if (items.length > 365) {
        setMessage({ type: 'error', text: 'Maximum 365 items per import' });
        setLoading(false);
        return;
      }

      setBulkProgress({ current: 0, total: items.length });

      const response = await faithService.bulkImportContent(items);
      
      setBulkProgress(null);
      setMessage({
        type: 'success',
        text: `${response.data.data.imported} items imported successfully. ${response.data.data.skipped || 0} duplicates skipped.`
      });
      setBulkItems('');
    } catch (error) {
      setBulkProgress(null);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Bulk import failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedContent = async (reset) => {
    setMessage(null);
    if (reset) {
      const confirmed = window.confirm('This will reset all existing faith content. Continue?');
      if (!confirmed) return;
    }

    setSeedLoading(true);
    try {
      const response = await faithService.seedContent(reset);
      const data = response.data;
      setMessage({
        type: 'success',
        text: `Faith content seeded. Entries: ${data.entries}, Seeded: ${data.seeded}, Skipped: ${data.skipped}`
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Faith content seeding failed'
      });
    } finally {
      setSeedLoading(false);
    }
  };

  const loadContentList = async (phase) => {
    setListLoading(true);
    try {
      const response = await faithService.getPhaseContent(phase);
      setContentList(response.data.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to load content list'
      });
      setContentList([]);
    } finally {
      setListLoading(false);
    }
  };

  // Check admin permission
  if (user?.role !== 'admin') {
    return (
      <>
        <Navigation />
        <main className="mx-auto max-w-[1920px] px-6 py-10">
          <Card>
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-error-700">Access Denied</h2>
              <p className="mt-2 text-neutral-600">Admin access required</p>
            </div>
          </Card>
        </main>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-[1920px] px-6 py-10">
        <Card>
          <h2 className="text-3xl font-display font-semibold text-neutral-900">Faith Content Management</h2>
          <p className="mt-2 text-neutral-600">Create, edit, and manage daily faith content and devotionals.</p>

          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              type="button"
              variant="primary"
              disabled={seedLoading}
              onClick={() => handleSeedContent(false)}
            >
              {seedLoading ? 'Seeding...' : 'Seed Faith Content'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={seedLoading}
              onClick={() => handleSeedContent(true)}
            >
              {seedLoading ? 'Seeding...' : 'Reset and Seed'}
            </Button>
          </div>

          {message && (
            <div
              className={`mt-4 rounded-soft border p-3 text-sm ${
                message.type === 'success'
                  ? 'border-success-200 bg-success-50 text-success-700'
                  : 'border-error-200 bg-error-50 text-error-700'
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Tabs */}
          <div className="mt-6 flex gap-2 border-b border-neutral-200">
            <button
              onClick={() => setActiveTab('create')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'create'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Create Content
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'list'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              View Content
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'bulk'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Bulk Import
            </button>
          </div>

          {/* Tab: Create Content */}
          {activeTab === 'create' && (
            <form onSubmit={handleCreateContent} className="mt-6 space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-neutral-800">Basic Information</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Input
                    label="Day (1-365)"
                    name="day"
                    type="number"
                    min="1"
                    max="365"
                    value={formData.day}
                    onChange={handleFormChange}
                    required
                  />
                  <Input
                    label="Phase (1-4)"
                    name="phase"
                    type="number"
                    min="1"
                    max="4"
                    value={formData.phase}
                    onChange={handleFormChange}
                    required
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Tradition</label>
                    <select
                      name="tradition"
                      value={formData.tradition}
                      onChange={handleFormChange}
                      className="mt-1 block w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                    >
                      {FAITH_TRADITION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Estimated Time (min)"
                    name="estimatedTime"
                    type="number"
                    min="5"
                    max="30"
                    value={formData.estimatedTime}
                    onChange={handleFormChange}
                  />
                </div>
              </section>

              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-neutral-800">Primary Teaching</h3>
                <div className="mt-4 space-y-4">
                  <Input
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="e.g., Finding Peace in the Storm"
                    required
                  />
                  <Input
                    label="Scripture Reference"
                    name="scriptureReference"
                    value={formData.scriptureReference}
                    onChange={handleFormChange}
                    placeholder="e.g., John 3:16"
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">
                      Scripture Text ({formData.scriptureText.length}/2000)
                    </label>
                    <textarea
                      name="scriptureText"
                      value={formData.scriptureText}
                      onChange={handleFormChange}
                      maxLength="2000"
                      rows="4"
                      className="mt-1 block w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                      placeholder="Full scripture passage"
                    />
                  </div>
                  <Input
                    label="Teaching Reference"
                    name="teachingReference"
                    value={formData.teachingReference}
                    onChange={handleFormChange}
                    placeholder="e.g., Root Teaching 1"
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">
                      Source Teaching ({formData.sourceTeaching.length}/2000)
                    </label>
                    <textarea
                      name="sourceTeaching"
                      value={formData.sourceTeaching}
                      onChange={handleFormChange}
                      maxLength="2000"
                      rows="4"
                      className="mt-1 block w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                      placeholder="Primary teaching text when scripture is not used"
                    />
                  </div>
                </div>
              </section>

              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-neutral-800">Content</h3>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">
                      Devotional Content ({formData.devotionalContent.length}/3000)
                    </label>
                    <textarea
                      name="devotionalContent"
                      value={formData.devotionalContent}
                      onChange={handleFormChange}
                      maxLength="3000"
                      rows="4"
                      className="mt-1 block w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                      placeholder="Devotional reflection on the scripture"
                      required
                    />
                  </div>
                  <Input
                    label="Wellness Connection"
                    name="recoveryConnection"
                    value={formData.recoveryConnection}
                    onChange={handleFormChange}
                    placeholder="How does this scripture relate to mental wellness?"
                  />
                </div>
              </section>

              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-neutral-800">Prompts & Themes</h3>
                <div className="mt-4 space-y-4">
                  <Input
                    label="Reflection Prompt"
                    name="reflectionPrompt"
                    value={formData.reflectionPrompt}
                    onChange={handleFormChange}
                    placeholder="What does this passage mean to you?"
                  />
                  <Input
                    label="Prayer Prompt"
                    name="prayerPrompt"
                    value={formData.prayerPrompt}
                    onChange={handleFormChange}
                    placeholder="Prayer reflection question"
                  />
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Theme Tag</label>
                      <select
                        name="themeTag"
                        value={formData.themeTag}
                        onChange={handleFormChange}
                        className="mt-1 block w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                      >
                        <option value="grace">Grace</option>
                        <option value="hope">Hope</option>
                        <option value="forgiveness">Forgiveness</option>
                        <option value="healing">Healing</option>
                        <option value="strength">Strength</option>
                        <option value="peace">Peace</option>
                        <option value="faith">Faith</option>
                        <option value="purpose">Purpose</option>
                        <option value="community">Community</option>
                        <option value="transformation">Transformation</option>
                        <option value="surrender">Surrender</option>
                        <option value="growth">Growth</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleFormChange}
                        className="mt-1 block w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                </div>
              </section>

              <Button type="submit" disabled={loading} className="w-full md:w-auto">
                {loading ? 'Creating...' : 'Create Content'}
              </Button>
            </form>
          )}

          {/* Tab: View Content */}
          {activeTab === 'list' && (
            <div className="mt-6 space-y-4">
              <div className="grid gap-2 md:grid-cols-4">
                {[1, 2, 3, 4].map(phase => (
                  <Button
                    key={phase}
                    onClick={() => loadContentList(phase)}
                    variant={listLoading ? 'secondary' : 'primary'}
                    className="w-full"
                  >
                    Phase {phase}
                  </Button>
                ))}
              </div>

              {listLoading ? (
                <div className="text-center py-8">Loading content...</div>
              ) : contentList.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-neutral-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-700">Day</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-700">Title</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-700">Tradition</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-700">Theme</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contentList.map(item => (
                        <tr key={item._id} className="border-b border-neutral-100 hover:bg-neutral-50">
                          <td className="px-4 py-3 text-neutral-900">{item.day}</td>
                          <td className="px-4 py-3 text-neutral-900">{item.title}</td>
                          <td className="px-4 py-3 text-neutral-600 capitalize">{item.tradition}</td>
                          <td className="px-4 py-3 text-neutral-600 capitalize">{item.themeTag}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block rounded-soft px-2 py-1 text-xs font-medium ${
                                item.status === 'published'
                                  ? 'bg-success-100 text-success-700'
                                  : 'bg-neutral-100 text-neutral-600'
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-500">No content found. Select a phase to view.</div>
              )}
            </div>
          )}

          {/* Tab: Bulk Import */}
          {activeTab === 'bulk' && (
            <form onSubmit={handleBulkImport} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700">
                  Import JSON (Array of Content Objects)
                </label>
                <p className="mt-1 text-xs text-neutral-500">
                  Max 365 items. Each must have: day, phase, tradition, title, devotionalContent, and either scripture fields or teaching fields
                </p>
                <textarea
                  value={bulkItems}
                  onChange={(e) => setBulkItems(e.target.value)}
                  rows="12"
                  className="mt-2 block w-full rounded border border-neutral-300 px-3 py-2 font-mono text-sm"
                  placeholder={`[
  {
    "day": 1,
    "phase": 1,
    "tradition": "christian",
    "title": "New Beginnings",
    "scriptureReference": "2 Corinthians 5:17",
    "scriptureText": "Therefore, if anyone is in Christ, he is a new creation...",
    "teachingReference": "",
    "sourceTeaching": "",
    "devotionalContent": "Today marks a new chapter in your mental wellness journey...",
    "reflectionPrompt": "What does transformation mean to you?",
    "prayerPrompt": "Pray for wisdom in your new beginning",
    "recoveryConnection": "In mental wellness, we too become new creations...",
    "themeTag": "transformation",
    "estimatedTime": 10,
    "status": "published"
  }
]`}
                />
              </div>

              {bulkProgress && (
                <div className="rounded bg-neutral-100 p-3">
                  <div className="text-sm font-medium text-neutral-700">
                    Progress: {bulkProgress.current} / {bulkProgress.total}
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-300">
                    <div
                      className="h-full bg-primary-600 transition-all"
                      style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Importing...' : 'Import Content'}
              </Button>
            </form>
          )}
        </Card>
      </main>
    </>
  );
};

export default FaithContentAdmin;
